import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { RecordPicker } from '../../components/RecordPicker.js';

interface DeploymentRow {
  readonly deploymentId: string;
  readonly modelCode: string | null;
  readonly modelName: string | null;
  readonly deploymentType: string;
  readonly status: string;
  readonly approvalStatus: string;
  readonly deployedAt: string | null;
}

const DEPLOYMENT_STATUSES = ['', 'PENDING', 'SCHEDULED', 'DEPLOYED', 'ROLLED_BACK', 'SUPERSEDED'];
const DEPLOYMENT_TYPES = ['IMMEDIATE', 'SCHEDULED', 'VERSION', 'ROLLBACK'];

/**
 * M25 — Model Deployment Dashboard (§8).
 * Tracks model deployments through their maker-checker lifecycle (BR-02):
 * PENDING → DEPLOYED on approval, with rollback support (BR-07). Shows the
 * current state of every deployment so operators can see what is live.
 */
export function M25ModelDeploymentDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<DeploymentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [modelId, setModelId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [deploymentType, setDeploymentType] = useState('IMMEDIATE');

  const load = useCallback((): void => {
    setLoading(true);
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    apiRequest<{ data: DeploymentRow[]; total: number }>(`/v1/ai/deployments${query}`, { token })
      .then((r) => { setRows(r.data); setTotal(r.total); setError(null); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token, status]);

  useEffect(() => { load(); }, [load]);

  function onDeploy(e: FormEvent): void {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest('/v1/ai/deployments', {
      method: 'POST',
      token,
      body: { modelId, versionId, deploymentType },
    })
      .then(() => { setNotice(tr('m25.deployment.deploymentCreated')); setModelId(''); setVersionId(''); load(); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setBusy(false));
  }

  function approve(deploymentId: string): void {
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest(`/v1/ai/deployments/${encodeURIComponent(deploymentId)}/approve`, { method: 'POST', token })
      .then(() => { setNotice(tr('m25.deployment.deploymentApproved')); load(); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }

  function rollback(deploymentId: string): void {
    const reason = window.prompt(tr('m25.deployment.rollbackReason'));
    if (reason === null || reason.trim().length === 0) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest(`/v1/ai/deployments/${encodeURIComponent(deploymentId)}/rollback`, {
      method: 'POST',
      token,
      body: { reason: reason.trim() },
    })
      .then(() => { setNotice(tr('m25.deployment.deploymentRolledBack')); load(); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }

  return (
    <main aria-label="deployment-dashboard">
      <h1>{tr('m25.deployment.title')}</h1>
      <form aria-label="deployment-create-form" onSubmit={onDeploy}>
        <RecordPicker<{ modelId: string; modelCode: string; modelName: string }>
          endpoint="/v1/ai/models?limit=200"
          idField="modelId"
          label={tr('m25.deployment.modelId')}
          testId="deploy-model-id"
          value={modelId}
          optionLabel={(m) => `${m.modelCode} \u2014 ${m.modelName}`}
          onPick={(id) => { setModelId(id); setVersionId(''); }}
        />
        <RecordPicker<{ versionId: string; versionNumber: string }>
          endpoint={modelId ? `/v1/ai/versions/model/${encodeURIComponent(modelId)}` : ''}
          idField="versionId"
          label={tr('m25.deployment.versionId')}
          testId="deploy-version-id"
          value={versionId}
          optionLabel={(v) => v.versionNumber}
          onPick={(id) => setVersionId(id)}
          disabled={!modelId}
        />
        <label htmlFor="deploy-type">{tr('m25.deployment.deploymentType')}</label>
        <select id="deploy-type" value={deploymentType} onChange={(e) => setDeploymentType(e.target.value)}>
          {DEPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="submit" disabled={busy || !modelId || !versionId}>{tr('m25.deployment.deploy')}</button>
      </form>
      <label htmlFor="deployment-status-filter">{tr('m25.deployment.filterLabel')}</label>
      <select id="deployment-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
        {DEPLOYMENT_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m25.deployment.allStatuses')}</option>)}
      </select>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m25.deployment.loading')}</p> : (
        <>
          <p aria-label="deployment-count">{tr('m25.deployment.count', { count: total })}</p>
          <table aria-label="deployment-table">
            <thead>
              <tr><th>{tr('m25.deployment.colModel')}</th><th>{tr('m25.deployment.colType')}</th><th>{tr('m25.deployment.colStatus')}</th><th>{tr('m25.deployment.colApproval')}</th><th>{tr('m25.deployment.colDeployedAt')}</th><th>{tr('m25.deployment.colActions')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.deploymentId}>
                  <td>{[r.modelCode, r.modelName].filter(Boolean).join(' \u2014 ')}</td>
                  <td>{r.deploymentType}</td>
                  <td>{r.status}</td>
                  <td>{r.approvalStatus}</td>
                  <td>{at(r.deployedAt)}</td>
                  <td>
                    <button type="button" aria-label={`approve-${r.deploymentId}`} disabled={busy} onClick={() => approve(r.deploymentId)}>{tr('m25.deployment.approve')}</button>
                    <button type="button" aria-label={`rollback-${r.deploymentId}`} disabled={busy} onClick={() => rollback(r.deploymentId)}>{tr('m25.deployment.rollback')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
